import { type CustomTooltipProps } from "ag-grid-react"

import HeaderTooltip from "./HeaderTooltip"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { Card, CardContent, CardHeader, CardTitle } from "../../Card"
import { filterDefined } from "~/utils/tsUtils"
import { Separator } from "../../Separator"
import { formatNumber } from "~/utils/formatting"
import {
  type IncomingOrdersMapValue,
  type PurchaseOrdersMapValue,
} from "~/hooks/useCollectionTableData"

type TooltipValue = {
  variantIds: string[]
  incomingOrdersMap?: Map<string, IncomingOrdersMapValue>
  purchaseOrdersMap?: Map<string, PurchaseOrdersMapValue>
  isComparison?: boolean
  originalName?: string
  comparisonName?: string
  originalIncomingOrdersMap?: Map<string, IncomingOrdersMapValue>
  originalPurchaseOrdersMap?: Map<string, PurchaseOrdersMapValue>
}

function aggregateOrders(
  variantIds: string[],
  incomingOrdersMap?: Map<string, IncomingOrdersMapValue>,
  purchaseOrdersMap?: Map<string, PurchaseOrdersMapValue>,
) {
  const incomingOrders = filterDefined(
    variantIds.map((variantId) => incomingOrdersMap?.get(variantId)),
  )
  const incomingTotal = incomingOrders.reduce((sum, entry) => sum + (entry?.total ?? 0), 0)
  const incomingIncoming = incomingOrders.reduce((sum, entry) => sum + (entry?.incoming ?? 0), 0)
  const incomingArrived = incomingOrders.reduce((sum, entry) => sum + (entry?.arrived ?? 0), 0)
  const incomingDelayed = incomingOrders.reduce((sum, entry) => sum + (entry?.delayed ?? 0), 0)

  const purchaseOrders = filterDefined(
    variantIds.map((variantId) => purchaseOrdersMap?.get(variantId)),
  )
  const poTotal = purchaseOrders.reduce((sum, entry) => sum + (entry?.total ?? 0), 0)
  const poDraft = purchaseOrders.reduce((sum, entry) => sum + (entry?.draft ?? 0), 0)
  const poSent = purchaseOrders.reduce((sum, entry) => sum + (entry?.sent ?? 0), 0)
  const poRegistered = purchaseOrders.reduce((sum, entry) => sum + (entry?.registered ?? 0), 0)
  const poDelivered = purchaseOrders.reduce((sum, entry) => sum + (entry?.delivered ?? 0), 0)
  const grandTotal = incomingTotal + poSent

  return {
    incomingTotal,
    incomingIncoming,
    incomingArrived,
    incomingDelayed,
    poTotal,
    poDraft,
    poSent,
    poRegistered,
    poDelivered,
    grandTotal,
    hasIncomingOrders: incomingTotal > 0,
    hasPurchaseOrders: poTotal > 0,
  }
}

type OrderBreakdownProps = {
  title?: string
  data: ReturnType<typeof aggregateOrders>
  showDraft?: boolean
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between items-center gap-6">
      <span className="text-fg-subtle text-sm">{label}</span>
      <span className={`text-sm tabular-nums ${value === 0 ? "text-fg-muted" : "text-fg-base"}`}>
        {formatNumber(value)}
      </span>
    </div>
  )
}

function OrderBreakdown({ title, data, showDraft = true }: OrderBreakdownProps) {
  const {
    grandTotal,
    hasIncomingOrders,
    hasPurchaseOrders,
    incomingIncoming,
    incomingArrived,
    incomingDelayed,
    poDraft,
    poSent,
    poRegistered,
    poDelivered,
  } = data

  return (
    <div className="flex-1 flex flex-col gap-2">
      {title && <div className="text-sm font-medium">{title}</div>}

      <div className="flex justify-between items-center gap-6">
        <span className="text-fg-subtle text-sm">Total</span>
        <span className="text-sm font-bold tabular-nums">{formatNumber(grandTotal)}</span>
      </div>

      {(hasIncomingOrders || hasPurchaseOrders) && <div className="border-t border-base" />}

      {hasIncomingOrders && (
        <div className="flex flex-col gap-1">
          <div className="text-xs font-semibold text-fg-base">Incoming Orders</div>
          <Row label="Incoming" value={incomingIncoming} />
          <Row label="Arrived" value={incomingArrived} />
          {incomingDelayed > 0 && <Row label="Delayed" value={incomingDelayed} />}
        </div>
      )}

      {hasPurchaseOrders && (
        <div className="flex flex-col gap-1">
          <div className="text-xs font-semibold text-fg-base">Purchase Orders</div>
          {showDraft && <Row label="Draft" value={poDraft} />}
          <Row label="Sent" value={poSent} />
          <Row label="Registered" value={poRegistered} />
          <Row label="Delivered" value={poDelivered} />
        </div>
      )}

      {!hasIncomingOrders && !hasPurchaseOrders && (
        <div className="text-fg-subtle text-sm text-center">No orders</div>
      )}
    </div>
  )
}

export default function IncomingOrdersCollectionTooltip(
  props: CustomTooltipProps<IVariantSummary, TooltipValue>,
) {
  if (props.location === "header") return <HeaderTooltip {...props} />

  if (!props.value?.variantIds) return

  const variantIds: string[] = props.value?.variantIds || []
  const isComparison = props.value?.isComparison ?? false

  if (props.location === "cell") {
    if (isComparison) {
      const originalData = aggregateOrders(
        variantIds,
        props.value?.originalIncomingOrdersMap,
        props.value?.originalPurchaseOrdersMap,
      )
      const comparisonData = aggregateOrders(
        variantIds,
        props.value?.incomingOrdersMap,
        props.value?.purchaseOrdersMap,
      )

      return (
        <div className="bg-base w-[420px] mr-6 z-10">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Purchase Order Units Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <OrderBreakdown
                  title={props.value?.originalName ?? "Original"}
                  data={originalData}
                  showDraft={true}
                />
                <Separator orientation="vertical" className="h-auto" />
                <OrderBreakdown
                  title={props.value?.comparisonName ?? "Comparison"}
                  data={comparisonData}
                  showDraft={false}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }

    const data = aggregateOrders(
      variantIds,
      props.value?.incomingOrdersMap,
      props.value?.purchaseOrdersMap,
    )

    return (
      <div className="bg-base w-[220px] mr-6 z-10">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Purchase Order Units</CardTitle>
          </CardHeader>
          <CardContent>
            <OrderBreakdown data={data} showDraft={true} />
          </CardContent>
        </Card>
      </div>
    )
  }
}
